import React, { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { 
  Sidebar, 
  SidebarContent, 
  SidebarHeader, 
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar
} from "@/components/ui/sidebar";
import { ChevronDown, ChevronUp, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

interface SidebarItemProps {
  icon: ReactNode;
  label: string;
  hasSubmenu?: boolean;
  isOpen?: boolean;
  submenuItems?: Array<{
    label: string;
    icon?: ReactNode;
    onClick?: () => void;
    isSelected?: boolean;
  }>;
  onClick?: () => void;
  isSelected?: boolean;
  linkTo?: string;
  className?: string;
}

interface DashboardSidebarProps {
  profile: {
    company_name: string | null;
    first_name?: string | null;
    last_name?: string | null;
  } | null;
  sidebarItems: SidebarItemProps[];
  activeView?: string;
}

export const DashboardSidebar = ({
  profile,
  sidebarItems,
  activeView
}: DashboardSidebarProps) => {
  const { t } = useTranslation();
  
  return (
    <Sidebar variant="sidebar" collapsible="icon">
      <SidebarHeader className="border-b border-sidebar-border flex h-16 items-center px-4 bg-white">
        <div className="flex items-center gap-3">
          <img src="/lovable-uploads/bc4b57d4-e29b-4e44-8e1c-82ec09ca6fd6.png" alt="Logo" className="h-8 w-8" />
          <span className="font-medium text-gebeya-purple">
            {profile?.company_name || 
             (profile?.first_name && profile?.last_name 
              ? `${profile.first_name} ${profile.last_name}` 
              : profile?.first_name || profile?.last_name || "Dashboard")}
          </span>
        </div>
      </SidebarHeader>
      
      <SidebarContent className="bg-white">
        <SidebarGroup>
          <SidebarGroupLabel>{t('navigation.mainMenu')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {sidebarItems.map((item) => (
                <SidebarMenuItem key={item.label}>
                  {item.hasSubmenu ? (
                    <div className="space-y-1">
                      <SidebarMenuButton
                        variant="default"
                        isActive={item.isSelected}
                        tooltip={item.label}
                        onClick={() => {
                          if (item.onClick) item.onClick();
                        }}
                        className={cn(
                          item.isSelected ? "bg-gebeya-pink text-white" : "",
                          item.className || ""
                        )}
                      >
                        <span className={item.isSelected ? "text-white" : ""}>
                          {item.icon}
                        </span>
                        <span className={item.isSelected ? "flex-1 text-white" : "flex-1"}>
                          {item.label}
                        </span>
                        {item.hasSubmenu && (
                          <span className={item.isSelected ? "text-white" : ""}>
                            {item.isOpen ? 
                              <ChevronUp className="h-4 w-4 ml-2" /> : 
                              <ChevronDown className="h-4 w-4 ml-2" />
                            }
                          </span>
                        )}
                      </SidebarMenuButton>
                      
                      {item.isOpen && item.submenuItems && (
                        <div className="ml-8 mt-1 space-y-0.5 pl-2 border-l-2 border-gray-100 bg-white">
                          {item.submenuItems.map((subItem) => (
                            <button
                              key={subItem.label}
                              onClick={() => {
                                if (subItem.onClick) subItem.onClick();
                              }}
                              className={cn(
                                "w-full flex items-center justify-between p-2 text-sm rounded-md transition-all duration-200",
                                subItem.isSelected 
                                  ? "bg-gebeya-pink text-white font-medium" 
                                  : "text-gray-600 hover:bg-gray-100"
                              )}
                            >
                              <div className="flex items-center gap-3">
                                {subItem.icon && (
                                  <span className={subItem.isSelected ? "text-white" : "text-gray-500"}>
                                    {subItem.icon}
                                  </span>
                                )}
                                <span className={subItem.isSelected ? "text-white" : ""}>
                                  {subItem.label}
                                </span>
                              </div>
                              {subItem.isSelected && (
                                <ChevronRight className="h-4 w-4 text-white" />
                              )}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <SidebarMenuButton
                      variant="default"
                      isActive={item.isSelected || (item.label.toLowerCase() === activeView)}
                      tooltip={item.label}
                      onClick={() => {
                        if (item.onClick) item.onClick();
                      }}
                      className={cn(
                        item.isSelected || (item.label.toLowerCase() === activeView) ? "bg-gebeya-pink text-white" : "",
                        item.className || ""
                      )}
                    >
                      <span className={item.isSelected || (item.label.toLowerCase() === activeView) ? "text-white" : ""}>
                        {item.icon}
                      </span>
                      <span className={item.isSelected || (item.label.toLowerCase() === activeView) ? "text-white" : ""}>
                        {item.label}
                      </span>
                    </SidebarMenuButton>
                  )}
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
};
